import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowUp, Bot, Crown, Loader2, MessageSquareText, User } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import axios from "axios";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/context/LanguageContext";
import { getApiBaseUrl } from "@/lib/config";
import { sanitizeTextInput, stripControlCharsNoTrim } from "@/lib/security";
import { cn } from "@/lib/utils";

const API = getApiBaseUrl();

const getOrCreateChatSessionId = (videoId, contentKind) => {
  if (typeof window === "undefined" || !videoId) {
    return null;
  }
  const storageKey = `voxara-chat-session:${contentKind || "media"}:${videoId}`;
  const existing = window.sessionStorage.getItem(storageKey);
  if (existing) {
    return existing;
  }
  const generated = typeof window.crypto?.randomUUID === "function"
    ? window.crypto.randomUUID()
    : `chat-${videoId}-${Date.now()}`;
  window.sessionStorage.setItem(storageKey, generated);
  return generated;
};

const formatChatErrorDetail = (detail, fallback = "Something went wrong.") => {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map((item) => formatChatErrorDetail(item, "")).filter(Boolean);
    return parts.join(" · ") || fallback;
  }
  if (typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.detail === "string") return detail.detail;
    const parts = Object.values(detail).map((value) => formatChatErrorDetail(value, "")).filter(Boolean);
    return parts.join(" · ") || fallback;
  }
  return fallback;
};

const normalizeDocumentCitationLabel = (evidence = {}, index = 0) => {
  if (typeof evidence?.reference_label === "string" && evidence.reference_label.trim()) {
    return evidence.reference_label.trim();
  }
  if (Number.isFinite(evidence?.page_number)) {
    return `p. ${evidence.page_number}`;
  }
  if (Number.isFinite(evidence?.page_start) && Number.isFinite(evidence?.page_end)) {
    return evidence.page_start === evidence.page_end ? `p. ${evidence.page_start}` : `p. ${evidence.page_start}-${evidence.page_end}`;
  }
  if (Number.isFinite(evidence?.page_start)) {
    return `p. ${evidence.page_start}`;
  }
  return `p. ? · ${index + 1}`;
};

const buildDocumentCitationItems = (items = []) => {
  const uniqueItems = [];
  const seen = new Set();
  items.forEach((item, index) => {
    const label = normalizeDocumentCitationLabel(item, index);
    const key = `${item?.chunk_id || "chunk"}:${label}:${(item?.block_ids || []).join(",")}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniqueItems.push({ ...item, citationLabel: label });
    }
  });
  return uniqueItems.slice(0, 6);
};

const TranscriptChat = ({ videoId, isEnabled, className, contentKind = "media", onDocumentCitationClick = null, testIdPrefix = "chat" }) => {
  const { language } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [chatUsage, setChatUsage] = useState(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const isDocument = contentKind === "document";
  const scopedTestId = (suffix) => `${testIdPrefix}-${suffix}`;
  const chatSessionId = useMemo(() => getOrCreateChatSessionId(videoId, contentKind), [videoId, contentKind]);

  const translations = useMemo(
    () => ({
      en: {
        title: "AI research assistant",
        subtitle: isDocument
          ? "Ask about the document, its summary, or specific fragments."
          : "Ask about the transcript or explicitly request public examples, links, and outside resources.",
        welcome: isDocument ? "Ask the AI about the document." : "Ask the AI about the transcription.",
        placeholder: isDocument ? "Ask about the document or a specific fragment..." : "Ask about the video or request public links...",
        send: "Send",
        thinking: isDocument ? "Analyzing document…" : "Analyzing transcript and sources…",
        remaining: "questions remaining",
        webUsed: "Public web used",
        queryLabel: "Search query",
        upgrade: isDocument
          ? "You reached the current AI question limit for documents."
          : "Upgrade to Professional or Enterprise to use the AI assistant for this video.",
        upgradeButton: "Upgrade plan",
        limitReached: "Monthly question limit reached. It resets next month.",
        freeLimitReached: "You already used the 8 AI questions included in Free. Upgrade to a paid plan to keep using the assistant.",
        emptyState: isDocument
          ? "Start with a question about the document, its key fragments, or the summary."
          : "Start with a question about the transcript, key moments, summary, or ask for public examples.",
      },
      es: {
        title: "Asistente de investigación IA",
        subtitle: isDocument
          ? "Pregunta sobre el documento, su resumen o fragmentos específicos."
          : "Pregunta sobre la transcripción o pide explícitamente ejemplos, links y recursos públicos.",
        welcome: isDocument ? "Pregúntale a la IA sobre el documento." : "Pregúntale a la IA sobre la transcripción.",
        placeholder: isDocument ? "Pregunta sobre el documento o un fragmento específico..." : "Pregunta sobre el video o pide links públicos...",
        send: "Enviar",
        thinking: isDocument ? "Analizando documento…" : "Analizando transcripción y fuentes…",
        remaining: "preguntas restantes",
        webUsed: "Web pública usada",
        queryLabel: "Query de búsqueda",
        upgrade: isDocument
          ? "Llegaste al límite actual de preguntas de IA para documentos."
          : "Actualiza a Professional o Enterprise para usar el asistente de IA en este video.",
        upgradeButton: "Mejorar plan",
        limitReached: "Límite mensual de preguntas alcanzado. Se renueva el próximo mes.",
        freeLimitReached: "Ya usaste las 8 preguntas de IA incluidas en Free. Mejora a un plan pago para seguir usando el asistente.",
        emptyState: isDocument
          ? "Empieza con una pregunta sobre el documento, sus fragmentos clave o el resumen."
          : "Empieza con una pregunta sobre la transcripción, los key moments, el resumen o pide ejemplos públicos.",
      },
    }),
    [isDocument]
  );

  const t = translations[language] || translations.en;
  const isLimitReached = Boolean(chatUsage && chatUsage.questions_remaining <= 0);
  const showUpgradeState = !isEnabled || isLimitReached;
  const upgradeCopy = isLimitReached
    ? (chatUsage?.limit_scope === "lifetime" ? t.freeLimitReached : t.limitReached)
    : t.upgrade;

  useEffect(() => {
    if (!messages.length) {
      setMessages([{ role: "assistant", content: t.welcome }]);
    }
  }, [messages.length, t.welcome]);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const fetchChatUsage = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("voxara_token");
      const response = await axios.get(`${API}/chat/usage`, {
        params: { content_kind: contentKind },
        headers: { Authorization: `Bearer ${token}` },
      });
      setChatUsage(response.data);
    } catch (error) {
      console.error("Error fetching chat usage:", error);
    }
  }, [contentKind]);

  useEffect(() => {
    fetchChatUsage();
  }, [fetchChatUsage]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = sanitizeTextInput(inputValue, 2000);
    const newMessages = [...messages, { role: "user", content: userMessage }];
    setMessages(newMessages);
    setInputValue("");
    setIsLoading(true);

    try {
      const token = sessionStorage.getItem("voxara_token");
      const response = await axios.post(
        `${API}/videos/${videoId}/chat`,
        {
          video_id: videoId,
          message: userMessage,
          conversation_history: messages,
          session_id: chatSessionId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessages([
        ...newMessages,
        {
          role: "assistant",
          content: response.data.response,
          usedExternalSearch: response.data.used_external_search,
          externalSearchQuery: response.data.external_search_query,
          transcriptEvidence: response.data.transcript_evidence || [],
        },
      ]);
      setChatUsage((prev) => ({
        ...prev,
        questions_used: response.data.questions_used_this_month,
        questions_remaining: response.data.questions_remaining,
      }));
    } catch (error) {
      console.error("Chat error:", error);
      const detail = formatChatErrorDetail(error.response?.data?.detail, "Something went wrong.");
      setMessages([...newMessages, { role: "assistant", content: `❌ ${detail}` }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const renderMarkdown = (content) => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        p: ({ children }) => <p className="mb-3 text-base leading-8 text-foreground sm:text-[1.02rem]">{children}</p>,
        ul: ({ children }) => <ul className="mb-3 list-disc space-y-2 pl-5 text-base leading-8 text-foreground sm:text-[1.02rem]">{children}</ul>,
        ol: ({ children }) => <ol className="mb-3 list-decimal space-y-2 pl-5 text-base leading-8 text-foreground sm:text-[1.02rem]">{children}</ol>,
        li: ({ children }) => <li className="leading-8">{children}</li>,
        strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
        blockquote: ({ children }) => (
          <blockquote className="mb-4 rounded-r-2xl rounded-l-md border-l-2 border-primary/60 bg-primary/5 px-4 py-3 text-sm leading-7 text-muted-foreground sm:text-[0.98rem]">
            {children}
          </blockquote>
        ),
        code: ({ inline, children }) =>
          inline ? (
            <code className="rounded bg-muted/70 px-1.5 py-0.5 text-[0.95em] text-foreground">{children}</code>
          ) : (
            <code className="block overflow-x-auto rounded-2xl border border-border/50 bg-muted/30 px-4 py-3 text-sm text-foreground">{children}</code>
          ),
        a: ({ href, children }) => (
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            className="text-primary underline decoration-primary/30 underline-offset-4 transition-colors hover:text-primary/80"
            data-testid="chat-message-link"
          >
            {children}
          </a>
        ),
        table: ({ children }) => (
          <div className="mb-4 overflow-x-auto rounded-xl border border-border/60 bg-muted/20">
            <table className="min-w-full text-left text-base sm:text-[1.02rem]">{children}</table>
          </div>
        ),
        th: ({ children }) => <th className="border-b border-border/60 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{children}</th>,
        td: ({ children }) => <td className="border-b border-border/30 px-3 py-2 align-top text-base leading-8 text-foreground sm:text-[1.02rem]">{children}</td>,
      }}
    >
      {content}
    </ReactMarkdown>
  );

  return (
    <div className={cn("overflow-hidden rounded-[28px] border border-border/70 bg-background shadow-2xl shadow-black/40", className)} data-testid="chat-section">
      <div className="border-b border-border/60 bg-background/95 px-6 py-4 backdrop-blur-xl sm:px-8">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-border/60 bg-card/80 shadow-sm shadow-black/20">
                <MessageSquareText className="h-4.5 w-4.5 text-primary" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="text-lg font-semibold tracking-tight text-foreground" data-testid="chat-title">{t.title}</h2>
                <p className="mt-1 text-sm leading-5 text-muted-foreground">{t.subtitle}</p>
              </div>
            </div>
          </div>

          {chatUsage && (
            <Badge variant="outline" className="rounded-full border-border/60 bg-muted/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground" data-testid="chat-usage-badge">
              {chatUsage.questions_remaining} {t.remaining}
            </Badge>
          )}
        </div>
      </div>

      {showUpgradeState ? (
        <div className="flex min-h-[320px] flex-col items-center justify-center px-8 py-12 text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-border/60 bg-card/80 shadow-sm shadow-black/20">
            <Crown className="h-6 w-6 text-primary" />
          </div>
          <p className="mb-6 max-w-md text-sm leading-7 text-muted-foreground">{upgradeCopy}</p>
          <Button onClick={() => (window.location.href = "/pricing")} data-testid="chat-upgrade-btn">
            {t.upgradeButton}
          </Button>
        </div>
      ) : (
        <div className="flex h-[620px] max-h-[620px] flex-col overflow-hidden">
          <div ref={messagesContainerRef} className="flex-1 overflow-y-auto overscroll-y-contain px-6 py-5 sm:px-8 lg:px-10">
            <div className="flex w-full flex-col gap-5">
              {messages.length === 0 && (
                <div className="rounded-2xl border border-dashed border-border/60 bg-muted/20 p-6 text-sm text-muted-foreground">
                  {t.emptyState}
                </div>
              )}

              {messages.map((message, index) => (
                <div key={`${message.role}-${index}`} className={cn("flex", message.role === "user" ? "justify-end" : "justify-start")}>
                  <div className="max-w-[96%] xl:max-w-[92%]">
                    <div className="mb-1.5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                      {message.role === "user" ? <User className="h-3.5 w-3.5" strokeWidth={1.5} /> : <Bot className="h-3.5 w-3.5 text-primary" strokeWidth={1.5} />}
                      <span>{message.role === "user" ? "You" : "AI"}</span>
                    </div>
                    <div
                      className={cn(
                        "rounded-2xl border px-5 py-4 text-base shadow-sm sm:text-[1.02rem]",
                        message.role === "user"
                          ? "rounded-tr-sm border-primary/25 bg-primary/10 text-foreground"
                          : "rounded-tl-sm border-border/60 bg-card text-card-foreground"
                      )}
                      data-testid={message.role === "user" ? "chat-user-message" : "chat-assistant-message"}
                    >
                      {message.role === "assistant" ? renderMarkdown(message.content) : <p className="whitespace-pre-wrap text-base leading-8 text-foreground sm:text-[1.02rem]">{message.content}</p>}
                    </div>

                    {message.role === "assistant" && (message.usedExternalSearch || message.externalSearchQuery) && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {message.usedExternalSearch && (
                          <Badge variant="outline" className="rounded-full border-primary/25 bg-primary/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-primary" data-testid="chat-external-search-badge">
                            {t.webUsed}
                          </Badge>
                        )}
                        {message.externalSearchQuery && (
                          <Badge variant="outline" className="max-w-full truncate rounded-full border-border/60 bg-muted/30 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground" data-testid="chat-search-query-badge">
                            {t.queryLabel}: {message.externalSearchQuery}
                          </Badge>
                        )}
                      </div>
                    )}

                    {message.role === "assistant" && isDocument && Array.isArray(message.transcriptEvidence) && message.transcriptEvidence.length > 0 && (
                      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid={`chat-document-citations-${index}`}>
                        <span className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground" data-testid={`chat-document-citations-label-${index}`}>
                          {language === "es" ? "Citas" : "Citations"}
                        </span>
                        {buildDocumentCitationItems(message.transcriptEvidence).map((evidence, evidenceIndex) => (
                          <button
                            key={`${evidence.chunk_id || evidence.citationLabel}-${evidenceIndex}`}
                            type="button"
                            onClick={() => onDocumentCitationClick?.(evidence)}
                            className="rounded-full border border-primary/20 bg-primary/8 px-3 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/14 hover:text-primary"
                            data-testid={`chat-document-citation-button-${index}-${evidenceIndex}`}
                          >
                            {evidence.citationLabel}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="rounded-2xl rounded-tl-sm border border-border/60 bg-card px-5 py-4 text-base text-muted-foreground shadow-sm sm:text-[1.02rem]">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      <span>{t.thinking}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="shrink-0 border-t border-border/60 bg-background px-6 py-4 sm:px-8 lg:px-10">
            <div className="w-full">
              <div className="rounded-[22px] border border-border/60 bg-muted/20 p-1.5 shadow-sm shadow-black/15 transition-all focus-within:border-primary/50 focus-within:ring-1 focus-within:ring-primary/20">
                <Textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(event) => setInputValue(stripControlCharsNoTrim(event.target.value).slice(0, 2000))}
                  onKeyDown={handleKeyDown}
                  placeholder={chatUsage && chatUsage.questions_remaining <= 0 ? t.limitReached : t.placeholder}
                  disabled={!isEnabled || isLoading || (chatUsage && chatUsage.questions_remaining <= 0)}
                  maxLength={2000}
                  className="min-h-[64px] max-h-[112px] resize-none border-0 bg-transparent px-3 py-2.5 pr-14 text-base leading-6 text-foreground placeholder:text-muted-foreground/70 focus-visible:ring-0 sm:text-[1.02rem]"
                  data-testid={scopedTestId("input")}
                />
                <div className="flex justify-end pr-1 pb-1">
                  <Button
                    type="button"
                    onClick={sendMessage}
                    disabled={!isEnabled || !inputValue.trim() || isLoading || (chatUsage && chatUsage.questions_remaining <= 0)}
                    className="h-9 w-9 rounded-full bg-primary p-0 text-primary-foreground transition-colors hover:bg-primary/90"
                    data-testid={scopedTestId("send-btn")}
                    aria-label={t.send}
                  >
                    <ArrowUp className="h-4 w-4" strokeWidth={2.2} />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranscriptChat;